import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TICKET_STATUSES, TICKET_PRIORITIES, statusMeta, priorityMeta, formatDateShort, SLA_STATUS_META, formatSeconds } from "@/lib/tickets";
import { uploadFileChunked, downloadUrl, humanFileSize, isViewableInline } from "@/lib/attachments";
import AttachmentViewer from "@/components/AttachmentViewer";
import Modal from "@/components/Modal";
import CcInput from "@/components/CcInput";
import { ArrowLeft, Send, Lock, Trash2, Sparkles, Paperclip, FileText, Download, Upload, Clock, Eye, Pencil, X as XIcon, GitMerge, Check } from "lucide-react";
import { toast } from "sonner";

export default function TicketDetail() {
    const { id } = useParams();
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const isStaff = ["agent", "super_agent", "admin", "superadmin"].includes(user?.role);
    const canDelete = ["admin", "superadmin"].includes(user?.role);

    // Refresh user from server on mount: si el admin le cambió el rol en otra
    // sesión, el frontend lo aprende sin requerir re-login.
    useEffect(() => { refreshUser?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [data, setData] = useState(null);
    const isRequester = !!(data?.ticket?.requester_id && user?.user_id && data.ticket.requester_id === user.user_id);
    const [agents, setAgents] = useState([]);
    const [groups, setGroups] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [canned, setCanned] = useState([]);
    const [cannedOpen, setCannedOpen] = useState(false);
    const [cannedAnchor, setCannedAnchor] = useState(null); // {left, top} fixed coords
    const [cannedQuery, setCannedQuery] = useState("");
    const cannedBtnRef = useRef(null);
    const [body, setBody] = useState("");
    const [isPrivate, setIsPrivate] = useState(false);
    const [ccEmails, setCcEmails] = useState([]);
    const [ccFixedEditing, setCcFixedEditing] = useState(false);
    const [ccFixedDraft, setCcFixedDraft] = useState([]);
    const [ccFixedSaving, setCcFixedSaving] = useState(false);
    const [directory, setDirectory] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [viewerAttachment, setViewerAttachment] = useState(null);
    const [thirdPartyModal, setThirdPartyModal] = useState(false);
    const [thirdPartyName, setThirdPartyName] = useState("");
    const [thirdPartySaving, setThirdPartySaving] = useState(false);
    const [thirdPartySuggestions, setThirdPartySuggestions] = useState([]);
    const [pendingAttachments, setPendingAttachments] = useState([]); // [{attachment_id, filename, mime, size}]
    const [activeTab, setActiveTab] = useState("conversation"); // "conversation" | "critical_dates" | "audit"
    const [auditEntries, setAuditEntries] = useState(null);
    const [auditLoading, setAuditLoading] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [descDraft, setDescDraft] = useState("");
    const [descSaving, setDescSaving] = useState(false);
    const [showOriginalDesc, setShowOriginalDesc] = useState(false);
    const [mergeModalOpen, setMergeModalOpen] = useState(false);
    const [mergeTargetId, setMergeTargetId] = useState("");
    const [mergeSaving, setMergeSaving] = useState(false);
    const [mergeError, setMergeError] = useState("");
    const [editingSubject, setEditingSubject] = useState(false);
    const [subjectDraft, setSubjectDraft] = useState("");
    const [subjectSaving, setSubjectSaving] = useState(false);
    const [showSubjectHistory, setShowSubjectHistory] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data: d } = await api.get(`/tickets/${id}`);
            setData(d);
        } catch (err) { setError(formatApiError(err)); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Load audit timeline on demand (only when staff opens the tab, and re-load after edits)
    useEffect(() => {
        if (activeTab !== "audit") return;
        if (!isStaff) return;
        (async () => {
            setAuditLoading(true);
            try {
                const { data } = await api.get(`/tickets/${id}/audit`);
                setAuditEntries(data.entries || []);
            } catch (_e) { setAuditEntries([]); }
            finally { setAuditLoading(false); }
        })();
    }, [activeTab, id, isStaff, data?.ticket?.updated_at]);

    useEffect(() => {
        // Load companies for ALL roles so the Empresa badge can render the name.
        // Superadmin sees all (may need to reassign cross-company); other roles see only their visible ones.
        (async () => {
            try {
                const params = user?.role === "superadmin" ? {} : { scope: "mine" };
                const co = await api.get("/companies", { params });
                setCompanies(co.data.filter((x) => x.is_special !== "all"));
            } catch (_e) { /* ignore */ }
        })();
    }, [user?.role]);

    useEffect(() => {
        if (!isStaff) return;
        (async () => {
            try {
                const [u, g, c] = await Promise.all([
                    api.get("/users"),
                    api.get("/groups"),
                    api.get("/canned-responses"),
                ]);
                setAgents(u.data.filter((x) => ["agent", "admin", "superadmin"].includes(x.role)));
                setGroups(g.data);
                setCanned(c.data);
            } catch (_e) { /* ignore */ }
        })();
    }, [isStaff]);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/users/directory");
                setDirectory(r.data || []);
            } catch (_e) { /* ignore */ }
        })();
    }, []);

    if (error) return <div className="alert alert-error">{error}</div>;
    if (!data) return <div className="page-center" style={{ minHeight: 200 }}><span className="loader loader-lg" /></div>;

    const { ticket, messages, attachments = [] } = data;
    const s = statusMeta(ticket.status);
    const p = priorityMeta(ticket.priority);
    const slaFR = SLA_STATUS_META[ticket.sla_first_response_status] || SLA_STATUS_META["n/a"];
    const slaRes = SLA_STATUS_META[ticket.sla_resolution_status] || SLA_STATUS_META["n/a"];
    const ticketCompany = ticket.company_id ? companies.find((c) => c.company_id === ticket.company_id) : null;

    const deleteAttachment = async (a) => {
        if (!window.confirm(`¿Eliminar el adjunto "${a.filename}"? Esta acción no se puede deshacer.`)) return;
        try {
            await api.delete(`/attachments/${a.attachment_id}`);
            await load();
        } catch (err) { setError(formatApiError(err)); }
    };

    const canDeleteAttachment = (a) => {
        if (!user) return false;
        if (a.uploader_id === user.user_id) return true;
        if (["admin", "superadmin"].includes(user.role)) return true;
        return false;
    };

    const saveCcFixed = async () => {
        const list = ccFixedDraft || [];
        setCcFixedSaving(true); setError("");
        try {
            await api.patch(`/tickets/${id}`, { cc_emails: list });
            setCcFixedEditing(false);
            await load();
        } catch (err) { setError(formatApiError(err)); }
        finally { setCcFixedSaving(false); }
    };

    const sendMessage = async (e) => {
        e?.preventDefault();
        if (!body.trim() && pendingAttachments.length === 0) return;
        setSending(true); setError("");
        try {
            await api.post(`/tickets/${id}/messages`, {
                body: body.trim() || "(adjunto)",
                is_private: isPrivate,
                attachment_ids: pendingAttachments.map((a) => a.attachment_id),
                cc_emails: isPrivate ? [] : (ccEmails || []),
            });
            setBody(""); setIsPrivate(false); setPendingAttachments([]); setCcEmails([]);
            await load();
        } catch (err) { setError(formatApiError(err)); }
        finally { setSending(false); }
    };

    const sendAndSetWaiting = async (targetWaiting) => {
        if (!body.trim() && pendingAttachments.length === 0) return;
        if (targetWaiting === "third_party" && !ticket.waiting_third_party) {
            setThirdPartyName("");
            setThirdPartyModal(true);
            const params = ticket.company_id ? { company_id: ticket.company_id, limit: 5 } : { limit: 5 };
            api.get("/tickets/third-party-suggestions", { params })
                .then((r) => setThirdPartySuggestions(r.data || []))
                .catch(() => setThirdPartySuggestions([]));
            return;
        }
        setSending(true); setError("");
        try {
            // Send message + waiting_state atomically. Backend respects waiting_state
            // explícito sobre cualquier auto-cálculo basado en timestamps.
            await api.post(`/tickets/${id}/messages`, {
                body: body.trim() || "(adjunto)",
                is_private: isPrivate,
                attachment_ids: pendingAttachments.map((a) => a.attachment_id),
                cc_emails: isPrivate ? [] : (ccEmails || []),
                waiting_state: targetWaiting || undefined,
                waiting_third_party: targetWaiting === "third_party" ? (ticket.waiting_third_party || undefined) : undefined,
            });
            setBody(""); setIsPrivate(false); setPendingAttachments([]); setCcEmails([]);
            await load();
        } catch (err) { setError(formatApiError(err)); }
        finally { setSending(false); }
    };

    const patchTicket = async (patch) => {
        try {
            const { data: updated } = await api.patch(`/tickets/${id}`, patch);
            setData({ ticket: updated, messages, attachments });
        } catch (err) { setError(formatApiError(err)); }
    };

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setUploading(true); setError(""); setUploadProgress(0);
        try {
            for (const f of files) {
                // Upload chunked WITHOUT linking to ticket; we'll link on message send
                const att = await uploadFileChunked(f, { onProgress: (p) => setUploadProgress(p) });
                setPendingAttachments((prev) => [...prev, att]);
            }
        } catch (err) { setError(err.response?.data?.detail || err.message || "Error al subir"); }
        finally { setUploading(false); setUploadProgress(0); e.target.value = ""; }
    };

    const removePending = (attId) => {
        setPendingAttachments((prev) => prev.filter((a) => a.attachment_id !== attId));
    };

    const removeTicket = async () => {
        if (!window.confirm(`¿Eliminar ticket ${id}? Acción irreversible.`)) return;
        try { await api.delete(`/tickets/${id}`); navigate("/tickets", { replace: true }); }
        catch (err) { setError(formatApiError(err)); }
    };

    const insertCanned = (c) => {
        setBody((prev) => (prev ? prev + "\n\n" : "") + c.body);
        setCannedOpen(false);
    };

    const openCanned = () => {
        const el = cannedBtnRef.current;
        if (el) {
            const r = el.getBoundingClientRect();
            const popupHeight = 360;
            const popupWidth = 360;
            const margin = 8;
            // Open upwards: popup bottom sits just above the button
            const top = Math.max(margin, r.top - popupHeight - margin);
            const left = Math.min(window.innerWidth - popupWidth - margin, Math.max(margin, r.left));
            setCannedAnchor({ top, left, width: popupWidth, height: popupHeight });
        }
        setCannedQuery("");
        setCannedOpen(true);
    };

    const author = (m) => {
        const isMine = m.author_id === user?.user_id;
        return (
            <div className="flex items-center gap-2">
                <div className="avatar" style={{ width: 26, height: 26, fontSize: 11 }}>{m.author_name?.[0]?.toUpperCase() || "·"}</div>
                <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{isMine ? "Tú" : m.author_name}</div>
                    <div className="text-dim mono" style={{ fontSize: 10.5 }}>{m.author_role}</div>
                </div>
            </div>
        );
    };

    const renderAttachment = (a) => {
        const viewable = isViewableInline(a.mime);
        const isImage = (a.mime || "").startsWith("image/");
        if (isImage) {
            return (
                <div key={a.attachment_id} data-testid={`attachment-${a.attachment_id}`} style={{ display: "inline-flex", flexDirection: "column", gap: 4, padding: 6, background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 10, maxWidth: 240 }}>
                    <button
                        type="button"
                        onClick={() => setViewerAttachment(a)}
                        data-testid={`attachment-view-${a.attachment_id}`}
                        title={`Ver ${a.filename}`}
                        style={{ background: "transparent", border: 0, padding: 0, cursor: "zoom-in", borderRadius: 8, overflow: "hidden", lineHeight: 0 }}
                    >
                        <img
                            src={downloadUrl(a.attachment_id, { inline: true })}
                            alt={a.filename}
                            loading="lazy"
                            style={{ display: "block", maxWidth: 224, maxHeight: 180, width: "auto", height: "auto", borderRadius: 8 }}
                        />
                    </button>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, padding: "0 4px" }}>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }} title={a.filename}>{a.filename}</div>
                            <div className="text-muted mono" style={{ fontSize: 10 }}>{humanFileSize(a.size)}</div>
                        </div>
                        <div className="flex" style={{ gap: 2 }}>
                            <a href={downloadUrl(a.attachment_id)} className="btn btn-ghost btn-sm" data-testid={`attachment-download-${a.attachment_id}`} title="Descargar" style={{ padding: 4 }}><Download size={12} /></a>
                            {canDeleteAttachment(a) && (
                                <button type="button" onClick={() => deleteAttachment(a)} className="btn btn-ghost btn-sm" data-testid={`attachment-delete-${a.attachment_id}`} title="Eliminar adjunto" style={{ padding: 4, color: "var(--accent)" }}><Trash2 size={12} /></button>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div key={a.attachment_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 8 }} data-testid={`attachment-${a.attachment_id}`}>
                <FileText size={16} color="var(--accent)" />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</div>
                    <div className="text-muted mono text-xs">{a.mime} · {humanFileSize(a.size)}</div>
                </div>
                <div className="flex gap-1">
                    {viewable && (
                        <button type="button" onClick={() => setViewerAttachment(a)} className="btn btn-ghost btn-sm" data-testid={`attachment-view-${a.attachment_id}`} title="Visualizar"><Eye size={12} /></button>
                    )}
                    <a href={downloadUrl(a.attachment_id)} className="btn btn-ghost btn-sm" data-testid={`attachment-download-${a.attachment_id}`} title="Descargar"><Download size={12} /></a>
                    {canDeleteAttachment(a) && (
                        <button type="button" onClick={() => deleteAttachment(a)} className="btn btn-ghost btn-sm" data-testid={`attachment-delete-${a.attachment_id}`} title="Eliminar adjunto" style={{ color: "var(--accent)" }}><Trash2 size={12} /></button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div data-testid={`ticket-detail-${id}`}>
            <div className="page-head" style={{ marginBottom: 18, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 480px", minWidth: 0 }}>
                    <Link to="/tickets" className="linklike" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}><ArrowLeft size={14} /> Tickets</Link>
                    <h1 style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <span className="mono" style={{ color: "var(--accent)" }}>{ticket.ticket_id}</span>
                        {!editingSubject ? (
                            <>
                                <span data-testid="ticket-subject">{ticket.subject}</span>
                                {Array.isArray(ticket.previous_subjects) && ticket.previous_subjects.length > 0 && (
                                    <button
                                        type="button"
                                        className="badge"
                                        onClick={() => setShowSubjectHistory(v => !v)}
                                        data-testid="ticket-subject-history-toggle"
                                        title="Ver asuntos anteriores"
                                        style={{ fontSize: 10, background: "rgba(99,102,241,.14)", borderColor: "rgba(99,102,241,.4)", color: "#A5B4FC", cursor: "pointer" }}
                                    >
                                        {ticket.previous_subjects.length} asunto{ticket.previous_subjects.length === 1 ? "" : "s"} anterior{ticket.previous_subjects.length === 1 ? "" : "es"}
                                    </button>
                                )}
                                {(user?.role === "admin" || user?.role === "superadmin") && (
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => { setSubjectDraft(ticket.subject || ""); setEditingSubject(true); }}
                                        data-testid="ticket-subject-edit-btn"
                                        title="Editar asunto"
                                        style={{ padding: "4px 8px" }}
                                    ><Pencil size={13} /></button>
                                )}
                            </>
                        ) : (
                            <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flex: 1, minWidth: 280 }}>
                                <input
                                    className="input"
                                    autoFocus
                                    value={subjectDraft}
                                    maxLength={200}
                                    onChange={(e) => setSubjectDraft(e.target.value)}
                                    data-testid="ticket-subject-input"
                                    style={{ flex: 1, minWidth: 200 }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    disabled={subjectSaving || !subjectDraft.trim() || subjectDraft.trim() === (ticket.subject || "").trim()}
                                    onClick={async () => {
                                        setSubjectSaving(true);
                                        try {
                                            await patchTicket({ subject: subjectDraft.trim() });
                                            setEditingSubject(false);
                                        } finally { setSubjectSaving(false); }
                                    }}
                                    data-testid="ticket-subject-save"
                                ><Check size={13} /></button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => { setEditingSubject(false); setSubjectDraft(""); }}
                                    disabled={subjectSaving}
                                    data-testid="ticket-subject-cancel"
                                ><XIcon size={13} /></button>
                            </span>
                        )}
                    </h1>
                    {showSubjectHistory && Array.isArray(ticket.previous_subjects) && ticket.previous_subjects.length > 0 && (
                        <div style={{ marginTop: 6, padding: "8px 12px", background: "var(--bg-1)", border: "1px dashed var(--border)", borderRadius: 6, fontSize: 12 }} data-testid="ticket-subject-history">
                            <div className="text-muted" style={{ marginBottom: 4, fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em" }}>📜 Asuntos anteriores</div>
                            {ticket.previous_subjects.slice().reverse().map((p, i) => (
                                <div key={i} style={{ padding: "3px 0", color: "var(--text)" }}>
                                    <span style={{ textDecoration: "line-through", color: "var(--text-muted)" }}>{p.subject || "(vacío)"}</span>
                                    <span className="text-dim" style={{ marginLeft: 8, fontSize: 11 }}>· por <b>{p.changed_by}</b> · {formatDateShort(p.changed_at)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex gap-2 items-center mt-2" style={{ flexWrap: "wrap" }}>
                        <p style={{ margin: 0, fontSize: 13 }}>Solicitado por <b>{ticket.requester_name}</b> · <span className="mono">{ticket.requester_email}</span> · {formatDateShort(ticket.created_at)}</p>
                        {ticket.opened_on_behalf && ticket.created_by_name && (
                            <span className="badge" data-testid="ticket-header-opened-by" style={{ background: "var(--bg-2)", borderColor: "var(--border-strong)", color: "var(--text)", padding: "3px 10px" }}>
                                Levantado por: <b style={{ marginLeft: 4 }}>{ticket.created_by_name}</b>
                                <span style={{ marginLeft: 4, color: "var(--text-muted)", fontSize: 11 }}>(agente)</span>
                            </span>
                        )}
                        {ticketCompany ? (
                            <span className="badge" data-testid="ticket-header-company" style={{ background: "var(--bg-2)", borderColor: "var(--border-strong)", color: "var(--text)", padding: "3px 10px" }}>
                                Empresa: <b style={{ marginLeft: 4 }}>{ticketCompany.name}</b>
                                {ticketCompany.is_placeholder && <span className="badge" style={{ marginLeft: 6, fontSize: 10 }}>Placeholder</span>}
                                {ticketCompany.is_confidential && <span className="badge" style={{ marginLeft: 6, fontSize: 10, background: "#991B1B", color: "#fff" }}>Confidencial</span>}
                            </span>
                        ) : (
                            <span className="badge" data-testid="ticket-header-company" style={{ background: "var(--bg-2)", borderColor: "var(--border-strong)", color: "var(--text-muted)", padding: "3px 10px" }}>
                                Empresa: <span className="text-dim" style={{ marginLeft: 4 }}>sin asignar</span>
                            </span>
                        )}
                    </div>
                </div>

                {/* Waiting state segmented control (right side, at ticket number height) */}
                {(() => {
                    const ws = ticket.waiting_state || "agent";
                    const opts = [
                        { v: "third_party", label: "3ros", color: "#DC2626", help: "Esperando a un tercero" },
                        { v: "agent", label: "Agente", color: "#F59E0B", help: "Esperando al agente" },
                        { v: "customer", label: "Cliente", color: "#16A34A", help: "Esperando al cliente" },
                    ];
                    const idx = opts.findIndex((o) => o.v === ws);
                    const sel = opts[idx >= 0 ? idx : 1];
                    const onPick = (val) => {
                        if (!isStaff || val === ws) return;
                        if (val === "third_party") {
                            setThirdPartyName(ticket.waiting_third_party || "");
                            setThirdPartyModal(true);
                            const params = ticket.company_id ? { company_id: ticket.company_id, limit: 5 } : { limit: 5 };
                            api.get("/tickets/third-party-suggestions", { params })
                                .then((r) => setThirdPartySuggestions(r.data || []))
                                .catch(() => setThirdPartySuggestions([]));
                            return;
                        }
                        patchTicket({ waiting_state: val });
                    };
                    return (
                        <div data-testid="ticket-waiting-state" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                            <span className="text-muted" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600 }}>Esperando a</span>
                            <div
                                role="radiogroup"
                                aria-label="Estado de espera"
                                style={{
                                    position: "relative",
                                    display: "grid",
                                    gridTemplateColumns: "repeat(3, minmax(72px, 1fr))",
                                    background: "var(--bg-2)",
                                    border: "1px solid var(--border-strong)",
                                    borderRadius: 999,
                                    padding: 3,
                                    overflow: "hidden",
                                    minWidth: 240,
                                }}
                            >
                                <div
                                    aria-hidden
                                    style={{
                                        position: "absolute",
                                        top: 3, bottom: 3,
                                        left: `calc(${(idx >= 0 ? idx : 1) * (100 / 3)}% + 3px)`,
                                        width: `calc(${100 / 3}% - 6px)`,
                                        background: sel.color,
                                        borderRadius: 999,
                                        transition: "left .18s ease, background .18s ease",
                                        boxShadow: `0 0 14px ${sel.color}66`,
                                    }}
                                />
                                {opts.map((o) => {
                                    const active = o.v === ws;
                                    return (
                                        <button
                                            key={o.v}
                                            type="button"
                                            role="radio"
                                            aria-checked={active}
                                            onClick={() => onPick(o.v)}
                                            disabled={!isStaff}
                                            title={o.help}
                                            data-testid={`ticket-waiting-${o.v}`}
                                            style={{
                                                position: "relative", zIndex: 1,
                                                background: "transparent", border: 0,
                                                padding: "7px 8px", fontSize: 11.5, fontWeight: 700,
                                                color: active ? "#fff" : "var(--text-muted)",
                                                cursor: isStaff ? "pointer" : "default",
                                                textTransform: "uppercase", letterSpacing: ".06em",
                                                transition: "color .18s ease",
                                            }}
                                        >
                                            {o.label}
                                        </button>
                                    );
                                })}
                            </div>
                            {ws === "third_party" ? (
                                <span data-testid="ticket-third-party-label" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "rgba(220,38,38,.18)", border: "1px solid rgba(220,38,38,.45)", borderRadius: 999, color: "#fff", fontSize: 12 }}>
                                    Tercero: <b>{ticket.waiting_third_party || "(sin especificar)"}</b>
                                    {isStaff && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setThirdPartyName(ticket.waiting_third_party || "");
                                                setThirdPartyModal(true);
                                                const params = ticket.company_id ? { company_id: ticket.company_id, limit: 5 } : { limit: 5 };
                                                api.get("/tickets/third-party-suggestions", { params })
                                                    .then((r) => setThirdPartySuggestions(r.data || []))
                                                    .catch(() => setThirdPartySuggestions([]));
                                            }}
                                            data-testid="ticket-third-party-edit"
                                            title="Editar tercero"
                                            style={{ background: "transparent", border: 0, color: "rgba(255,255,255,.85)", cursor: "pointer", padding: 0, marginLeft: 4, fontSize: 11, textDecoration: "underline" }}
                                        >
                                            editar
                                        </button>
                                    )}
                                </span>
                            ) : (
                                <span className="text-muted" style={{ fontSize: 11.5 }}>{sel.help}</span>
                            )}
                        </div>
                    );
                })()}
            </div>

            {error && <div className="alert alert-error" data-testid="ticket-detail-error">{error}</div>}

            {/* SLA badges + delete button row */}
            <div className="flex items-center" style={{ justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                <div className="flex gap-2 items-center" style={{ flexWrap: "wrap" }} data-testid="ticket-sla-badges">
                    {ticket.first_response_at ? (
                        <span className="badge" style={{ color: "#16A34A", borderColor: "rgba(22,163,74,.55)", background: "rgba(22,163,74,.12)" }} title="Primera respuesta" data-testid="ticket-first-response-badge">
                            <Clock size={11} /> 1ª respuesta: {formatSeconds(ticket.first_response_seconds)}
                        </span>
                    ) : (
                        ticket.sla_first_response_status && ticket.sla_first_response_status !== "n/a" && (
                            <span className="badge" style={{ color: slaFR.color, borderColor: slaFR.color + "66", background: slaFR.color + "18" }} title="SLA 1ª respuesta">
                                <Clock size={11} /> {slaFR.label} (1ª resp.)
                            </span>
                        )
                    )}
                    {ticket.sla_resolution_status && ticket.sla_resolution_status !== "n/a" && ticket.status !== "resolved" && ticket.status !== "closed" && (
                        <span className="badge" style={{ color: slaRes.color, borderColor: slaRes.color + "66", background: slaRes.color + "18" }} title="SLA resolución">
                            {slaRes.icon} {slaRes.label} (resolución)
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    {isStaff && !ticket.merged_into && (
                        <button className="btn btn-sm" onClick={() => { setMergeModalOpen(true); setMergeTargetId(""); setMergeError(""); }} data-testid="ticket-merge-btn"><GitMerge size={13} /> Fusionar</button>
                    )}
                    {canDelete && (
                        <button className="btn btn-danger btn-sm" onClick={removeTicket} data-testid="ticket-delete-btn"><Trash2 size={13} /> Eliminar</button>
                    )}
                </div>
            </div>

            {error && <div className="alert alert-error" data-testid="ticket-detail-error">{error}</div>}

            {/* Banner para tickets fusionados o que contienen fusiones */}
            {ticket.merged_into && (
                <div className="alert" style={{ background: "rgba(245,158,11,.08)", borderColor: "rgba(245,158,11,.5)", color: "#F59E0B" }} data-testid="ticket-merged-into-banner">
                    <GitMerge size={14} style={{ display: "inline", marginRight: 6 }} />
                    Este ticket está fusionado con <a href={`/tickets/${ticket.merged_into}`} style={{ color: "#FBBF24", textDecoration: "underline", marginLeft: 4 }} data-testid="ticket-merged-into-link">{ticket.merged_into}</a>. El historial activo se encuentra allí.
                </div>
            )}
            {Array.isArray(ticket.merged_ticket_ids) && ticket.merged_ticket_ids.length > 0 && (
                <div className="alert" style={{ background: "rgba(99,102,241,.08)", borderColor: "rgba(99,102,241,.4)", color: "#A5B4FC" }} data-testid="ticket-absorbed-banner">
                    <GitMerge size={14} style={{ display: "inline", marginRight: 6 }} />
                    Tickets fusionados aquí: {ticket.merged_ticket_ids.map((tid, i) => (
                        <span key={tid}>{i > 0 ? ", " : ""}<b>{tid}</b></span>
                    ))}
                </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 22, alignItems: "flex-start" }}>
                {/* Main timeline */}
                <div>
                    <div className="card">
                        <div className="card-header">
                            <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                Descripción
                                {ticket.original_description && (
                                    <span className="badge" style={{ fontSize: 10, background: "rgba(99,102,241,.12)", borderColor: "rgba(99,102,241,.4)", color: "#A5B4FC" }} data-testid="ticket-description-edited-badge" title={`Editado por ${ticket.description_edited_by || "—"} el ${formatDateShort(ticket.description_edited_at)}`}>
                                        Editado
                                    </span>
                                )}
                            </h3>
                            <div className="flex gap-2 items-center">
                                <span className="badge" style={{ color: s.color, borderColor: s.color + "66", background: s.color + "18" }}>{s.label}</span>
                                <span className="badge" style={{ color: p.color, borderColor: p.color + "66", background: p.color + "18" }}>{p.label}</span>
                                {(isStaff || isRequester) && !editingDesc && ticket.status !== "closed" && (
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => { setDescDraft(ticket.description || ""); setEditingDesc(true); }}
                                        data-testid="ticket-description-edit-btn"
                                        title="Editar descripción"
                                        style={{ padding: "4px 8px" }}
                                    ><Pencil size={13} /></button>
                                )}
                            </div>
                        </div>
                        {!editingDesc ? (
                            <div style={{ padding: 20, whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6, color: "var(--text)" }} data-testid="ticket-description">
                                {ticket.description}
                                {(() => {
                                    const orphans = (attachments || []).filter((a) => !a.message_id);
                                    if (orphans.length === 0) return null;
                                    return (
                                        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="ticket-description-attachments">
                                            {orphans.map((a) => renderAttachment(a))}
                                        </div>
                                    );
                                })()}
                                {ticket.original_description && (
                                    <div style={{ marginTop: 16, padding: "10px 14px", background: "var(--bg-1)", border: "1px dashed var(--border)", borderRadius: 8 }}>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-sm"
                                            onClick={() => setShowOriginalDesc(v => !v)}
                                            data-testid="ticket-description-toggle-original"
                                            style={{ fontSize: 12, padding: 0, color: "var(--text-muted)" }}
                                        >
                                            {showOriginalDesc ? "▼" : "►"} Ver descripción original
                                            {ticket.description_edited_by && <span style={{ marginLeft: 8 }}>(editado por {ticket.description_edited_by})</span>}
                                        </button>
                                        {showOriginalDesc && (
                                            <div style={{ marginTop: 10, padding: 12, background: "var(--bg-2)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, color: "var(--text-muted)", whiteSpace: "pre-wrap" }} data-testid="ticket-original-description">
                                                {ticket.original_description}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                                <textarea
                                    className="textarea"
                                    rows={6}
                                    value={descDraft}
                                    onChange={(e) => setDescDraft(e.target.value)}
                                    maxLength={20000}
                                    data-testid="ticket-description-textarea"
                                    autoFocus
                                />
                                <div className="flex gap-2 justify-end">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => { setEditingDesc(false); setDescDraft(""); }}
                                        disabled={descSaving}
                                        data-testid="ticket-description-cancel"
                                    ><XIcon size={13} /> Cancelar</button>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        disabled={descSaving || !descDraft.trim() || descDraft.trim() === (ticket.description || "").trim()}
                                        onClick={async () => {
                                            setDescSaving(true);
                                            try {
                                                await patchTicket({ description: descDraft.trim() });
                                                setEditingDesc(false);
                                            } finally { setDescSaving(false); }
                                        }}
                                        data-testid="ticket-description-save"
                                    ><Check size={13} /> {descSaving ? "Guardando…" : "Guardar"}</button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Pestañas: Conversación / Fechas críticas */}
                    <div className="mt-4">
                        <TicketTabs
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            conversationCount={messages.filter(m => !m.is_private || ["admin","superadmin","agent","super_agent"].includes(user?.role)).length}
                            criticalDatesCount={(ticket.critical_dates || []).filter(c => c.date || c.event).length}
                            showCriticalTab={true /* todos pueden ver, solo staff puede editar (controlado en el card) */}
                            showAuditTab={isStaff}
                        />
                    </div>

                    {activeTab === "critical_dates" && (
                        <CriticalDatesCard
                            ticket={ticket}
                            canEdit={isStaff}
                            onSave={async (rows) => { await patchTicket({ critical_dates: rows }); }}
                        />
                    )}

                    {activeTab === "audit" && isStaff && (
                        <TicketAuditCard entries={auditEntries} loading={auditLoading} />
                    )}

                    {activeTab === "conversation" && (
                        <div className="card">
                            <div className="card-header"><h3>Conversación ({messages.filter(m => !m.is_private || ["admin","superadmin","agent","super_agent"].includes(user?.role)).length})</h3></div>
                        <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                            {messages.length === 0 && (<div className="text-muted" style={{ padding: 20, textAlign: "center" }}>Aún no hay respuestas.</div>)}
                            {messages.map((m) => {
                                // Defensa en profundidad: si por algún motivo el backend envía una nota privada
                                // a un usuario no-staff (customer/super_customer), NO la renderizamos.
                                const isStaff = ["admin", "superadmin", "agent", "super_agent"].includes(user?.role);
                                const isAdmin = ["admin", "superadmin"].includes(user?.role);
                                if (m.is_private && !isStaff) return null;
                                const msgAtts = (attachments || []).filter((a) => a.message_id === m.message_id);
                                const isDeleted = !!m.is_deleted;
                                return (
                                <div
                                    key={m.message_id}
                                    className="card"
                                    style={{
                                        padding: 14,
                                        background: isDeleted ? "rgba(239,68,68,.06)" : (m.is_private ? "rgba(245,158,11,.06)" : "var(--bg-1)"),
                                        borderColor: isDeleted ? "rgba(239,68,68,.4)" : (m.is_private ? "rgba(245,158,11,.35)" : "var(--border)"),
                                        opacity: isDeleted ? 0.7 : 1,
                                    }}
                                    data-testid={`msg-${m.message_id}`}
                                >
                                    <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                                        {author(m)}
                                        <div className="flex gap-2 items-center">
                                            {isDeleted && (
                                                <span className="badge" data-testid={`msg-${m.message_id}-deleted-badge`} style={{ color: "#EF4444", borderColor: "rgba(239,68,68,.5)", background: "rgba(239,68,68,.1)", fontSize: 10 }}>
                                                    🗑️ Borrado por {m.deleted_by}
                                                </span>
                                            )}
                                            {m.is_private && <span className="badge" style={{ color: "#F59E0B", borderColor: "rgba(245,158,11,.5)", background: "rgba(245,158,11,.1)" }}><Lock size={10} /> Nota privada</span>}
                                            <span className="text-dim mono text-xs">{formatDateShort(m.created_at)}</span>
                                            {isAdmin && !m.is_system && !isDeleted && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-sm"
                                                    title="Borrar respuesta (soft delete; queda visible para admin)"
                                                    data-testid={`msg-${m.message_id}-delete-btn`}
                                                    style={{ padding: "3px 6px", color: "#EF4444" }}
                                                    onClick={async () => {
                                                        if (!window.confirm("¿Borrar esta respuesta? Quedará visible solo para admins y se puede restaurar.")) return;
                                                        try { await api.delete(`/tickets/${id}/messages/${m.message_id}`); await load(); }
                                                        catch (e) { toast.error(formatApiError(e, "No se pudo borrar")); }
                                                    }}
                                                ><Trash2 size={12} /></button>
                                            )}
                                            {isAdmin && isDeleted && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-sm"
                                                    title="Restaurar esta respuesta"
                                                    data-testid={`msg-${m.message_id}-restore-btn`}
                                                    style={{ padding: "3px 8px", color: "#22C55E", fontSize: 11 }}
                                                    onClick={async () => {
                                                        try { await api.post(`/tickets/${id}/messages/${m.message_id}/restore`); await load(); }
                                                        catch (e) { toast.error(formatApiError(e, "No se pudo restaurar")); }
                                                    }}
                                                >↺ Restaurar</button>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.55, textDecoration: isDeleted ? "line-through" : "none" }}>{m.body}</div>
                                    {msgAtts.length > 0 && (
                                        <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }} data-testid={`msg-${m.message_id}-attachments`}>
                                            {msgAtts.map((a) => renderAttachment(a))}
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>

                        {ticket.status !== "closed" && (
                            <form onSubmit={sendMessage} style={{ padding: 14, borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 10 }}>
                                <textarea className="textarea" value={body} onChange={(e) => setBody(e.target.value)} placeholder={isPrivate ? "Nota privada (solo visible para el staff)…" : "Escribe tu respuesta…"} rows={4} data-testid="ticket-reply-body" />
                                {!isPrivate && (
                                    <div data-testid="ticket-reply-cc" style={{ marginTop: -2 }}>
                                        <CcInput
                                            value={ccEmails}
                                            onChange={setCcEmails}
                                            suggestions={directory}
                                            placeholder={(ticket.cc_emails || []).length > 0
                                                ? `Cc adicional (los Cc fijos ya se copiarán)`
                                                : "Cc: empieza a escribir un nombre o correo…"}
                                            compact
                                            testid="ticket-reply-cc-input"
                                        />
                                    </div>
                                )}
                                {pendingAttachments.length > 0 && (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="ticket-pending-attachments">
                                        {pendingAttachments.map((a) => (
                                            <span key={a.attachment_id} title={`${a.mime} · ${humanFileSize(a.size)}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px 4px 10px", background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 999, fontSize: 12 }} data-testid={`pending-attachment-${a.attachment_id}`}>
                                                <Paperclip size={11} />
                                                <span style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                                                <button type="button" onClick={() => removePending(a.attachment_id)} title="Quitar" data-testid={`pending-attachment-remove-${a.attachment_id}`} style={{ background: "transparent", border: 0, color: "var(--text-muted)", cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="flex items-center justify-between">
                                    <div className="flex gap-3 items-center">
                                        <label className="btn btn-ghost btn-sm" style={{ cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? .6 : 1 }} data-testid="ticket-upload-label" title="Adjuntar archivos a este mensaje">
                                            {uploading ? <><span className="loader" /> {Math.round(uploadProgress)}%</> : <><Paperclip size={13} /> Adjuntar</>}
                                            <input type="file" multiple style={{ display: "none" }} accept="*/*" onChange={onUpload} disabled={uploading} data-testid="ticket-upload-input" />
                                        </label>
                                        {isStaff && (
                                            <label className="flex items-center gap-2 text-sm" style={{ cursor: "pointer" }}>
                                                <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} data-testid="ticket-reply-private" /> Nota privada
                                            </label>
                                        )}
                                        {isStaff && canned.length > 0 && (
                                            <>
                                                <button
                                                    ref={cannedBtnRef}
                                                    type="button"
                                                    className="btn btn-ghost btn-sm"
                                                    onClick={() => (cannedOpen ? setCannedOpen(false) : openCanned())}
                                                    data-testid="ticket-canned-btn"
                                                >
                                                    <Sparkles size={13} /> Plantillas
                                                </button>
                                                {cannedOpen && cannedAnchor && (
                                                    <>
                                                        {/* click-outside backdrop (transparent) */}
                                                        <div
                                                            onClick={() => setCannedOpen(false)}
                                                            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "transparent" }}
                                                        />
                                                        <div
                                                            data-testid="ticket-canned-popup"
                                                            style={{
                                                                position: "fixed",
                                                                top: cannedAnchor.top,
                                                                left: cannedAnchor.left,
                                                                width: cannedAnchor.width,
                                                                maxHeight: cannedAnchor.height,
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                background: "var(--bg-1)",
                                                                border: "1px solid var(--border-strong)",
                                                                borderRadius: 10,
                                                                zIndex: 1001,
                                                                boxShadow: "0 20px 40px -12px rgba(0,0,0,.5)",
                                                            }}
                                                        >
                                                            <div style={{ padding: 8, borderBottom: "1px solid var(--border)" }}>
                                                                <input
                                                                    autoFocus
                                                                    type="text"
                                                                    placeholder="Buscar plantilla por título o atajo…"
                                                                    value={cannedQuery}
                                                                    onChange={(e) => setCannedQuery(e.target.value)}
                                                                    data-testid="ticket-canned-search"
                                                                    style={{ width: "100%", padding: "8px 10px", background: "var(--bg-2)", border: "1px solid var(--border-strong)", borderRadius: 6, color: "var(--text)", fontSize: 13, outline: "none" }}
                                                                />
                                                            </div>
                                                            <div style={{ overflow: "auto", padding: 6, flex: 1 }}>
                                                                {(() => {
                                                                    const q = cannedQuery.trim().toLowerCase();
                                                                    const filtered = q
                                                                        ? canned.filter((c) =>
                                                                            (c.title || "").toLowerCase().includes(q) ||
                                                                            (c.shortcut || "").toLowerCase().includes(q) ||
                                                                            (c.body || "").toLowerCase().includes(q))
                                                                        : canned;
                                                                    if (filtered.length === 0) {
                                                                        return <div style={{ padding: 16, textAlign: "center", color: "var(--text-muted)", fontSize: 12.5 }}>Sin coincidencias</div>;
                                                                    }
                                                                    return filtered.map((c) => (
                                                                        <button
                                                                            type="button"
                                                                            key={c.canned_id}
                                                                            onClick={() => insertCanned(c)}
                                                                            data-testid={`canned-pick-${c.canned_id}`}
                                                                            style={{
                                                                                display: "block", width: "100%", textAlign: "left",
                                                                                padding: "8px 12px", marginBottom: 2,
                                                                                background: "transparent", border: "1px solid transparent",
                                                                                borderRadius: 6, cursor: "pointer",
                                                                            }}
                                                                            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-2)"; }}
                                                                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                                                        >
                                                                            <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 600, marginBottom: 2, display: "flex", justifyContent: "space-between", gap: 8 }}>
                                                                                <span>{c.title}</span>
                                                                                {c.shortcut && <span className="mono" style={{ fontSize: 10.5, padding: "1px 6px", background: "var(--bg-2)", borderRadius: 4, color: "var(--text-muted)" }}>/{c.shortcut}</span>}
                                                                            </div>
                                                                            <div style={{ fontSize: 11.5, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.body}</div>
                                                                        </button>
                                                                    ));
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                        {isStaff ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => sendAndSetWaiting("third_party")}
                                                    disabled={sending || (!body.trim() && pendingAttachments.length === 0)}
                                                    title="Enviar y marcar como Esperando a 3ros"
                                                    data-testid="ticket-reply-send-red"
                                                    style={{
                                                        padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                                                        background: "#DC2626", color: "#fff", border: "1px solid #DC2626",
                                                        cursor: (sending || (!body.trim() && pendingAttachments.length === 0)) ? "not-allowed" : "pointer",
                                                        opacity: (sending || (!body.trim() && pendingAttachments.length === 0)) ? 0.55 : 1,
                                                        display: "inline-flex", alignItems: "center", gap: 6,
                                                        boxShadow: "0 0 10px rgba(220,38,38,.35)",
                                                    }}
                                                >
                                                    <Send size={12} /> Enviar · 3ros
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => sendAndSetWaiting("agent")}
                                                    disabled={sending || (!body.trim() && pendingAttachments.length === 0)}
                                                    title="Enviar y marcar como Esperando al agente"
                                                    data-testid="ticket-reply-send-yellow"
                                                    style={{
                                                        padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                                                        background: "#F59E0B", color: "#1A1A1C", border: "1px solid #F59E0B",
                                                        cursor: (sending || (!body.trim() && pendingAttachments.length === 0)) ? "not-allowed" : "pointer",
                                                        opacity: (sending || (!body.trim() && pendingAttachments.length === 0)) ? 0.55 : 1,
                                                        display: "inline-flex", alignItems: "center", gap: 6,
                                                        boxShadow: "0 0 10px rgba(245,158,11,.35)",
                                                    }}
                                                >
                                                    <Send size={12} /> Enviar · Agente
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => sendAndSetWaiting("customer")}
                                                    disabled={sending || (!body.trim() && pendingAttachments.length === 0)}
                                                    title="Enviar y marcar como Esperando al cliente"
                                                    data-testid="ticket-reply-send-green"
                                                    style={{
                                                        padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                                                        background: "#16A34A", color: "#fff", border: "1px solid #16A34A",
                                                        cursor: (sending || (!body.trim() && pendingAttachments.length === 0)) ? "not-allowed" : "pointer",
                                                        opacity: (sending || (!body.trim() && pendingAttachments.length === 0)) ? 0.55 : 1,
                                                        display: "inline-flex", alignItems: "center", gap: 6,
                                                        boxShadow: "0 0 10px rgba(22,163,74,.35)",
                                                    }}
                                                >
                                                    <Send size={12} /> Enviar · Cliente
                                                </button>
                                            </>
                                        ) : (
                                            <button type="submit" className="btn btn-primary btn-sm" disabled={sending || (!body.trim() && pendingAttachments.length === 0)} data-testid="ticket-reply-submit">
                                                {sending ? <span className="loader" /> : <><Send size={13} /> Enviar</>}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </form>
                        )}
                        {ticket.status === "closed" && (
                            <div style={{ padding: 14, borderTop: "1px solid var(--border)", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                Este ticket está cerrado.
                                {isStaff && <> <button className="linklike" onClick={() => patchTicket({ status: "open" })} data-testid="ticket-reopen">Reabrir</button></>}
                            </div>
                        )}
                        </div>
                    )}
                </div>

                {/* Sidebar meta */}
                <div>
                    <div className="card card-padded" data-testid="ticket-sidebar">
                        <h3 style={{ margin: 0, fontSize: 14, marginBottom: 14, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".12em" }}>Detalles</h3>

                        <div className="field">
                            <label>Estado</label>
                            {isStaff || ticket.requester_id === user?.user_id ? (
                                <select className="select" value={ticket.status} onChange={(e) => patchTicket({ status: e.target.value })} data-testid="ticket-sidebar-status">
                                    {TICKET_STATUSES.map((st) => (
                                        <option key={st.v} value={st.v} disabled={!isStaff && st.v !== "closed" && st.v !== ticket.status}>{st.label}</option>
                                    ))}
                                </select>
                            ) : <div><span className="badge">{s.label}</span></div>}
                        </div>

                        {/* Waiting state segmented control: now rendered in the header */}

                        <div className="field mt-3">
                            <label>Prioridad</label>
                            {isStaff ? (
                                <select className="select" value={ticket.priority} onChange={(e) => patchTicket({ priority: e.target.value })} data-testid="ticket-sidebar-priority">
                                    {TICKET_PRIORITIES.map((pp) => <option key={pp.v} value={pp.v}>{pp.label}</option>)}
                                </select>
                            ) : <div><span className="badge">{p.label}</span></div>}
                        </div>

                        {isStaff && (
                            <div className="field mt-3" data-testid="ticket-sidebar-priority-rank-wrap">
                                <label>Orden de atención (0-999)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="999"
                                    step="1"
                                    inputMode="numeric"
                                    className="input"
                                    value={ticket.priority_rank ?? ""}
                                    placeholder="—"
                                    style={{ maxWidth: 110 }}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === "") { patchTicket({ priority_rank: null }); return; }
                                        const n = parseInt(raw, 10);
                                        if (Number.isNaN(n) || n < 0 || n > 999) return;
                                        patchTicket({ priority_rank: n });
                                    }}
                                    data-testid="ticket-sidebar-priority-rank"
                                />
                                <p className="text-muted text-xs" style={{ marginTop: 4 }}>Número de 3 cifras. Menor número = mayor urgencia. Se usa para ordenar la lista.</p>
                            </div>
                        )}

                        {isStaff && (
                            <>
                                <div className="field mt-3">
                                    <label>Asignado a</label>
                                    <select className="select" value={ticket.assignee_id || ""} onChange={(e) => patchTicket({ assignee_id: e.target.value || null })} data-testid="ticket-sidebar-assignee">
                                        <option value="">— sin asignar —</option>
                                        {agents.map((a) => <option key={a.user_id} value={a.user_id}>{a.name} ({a.role})</option>)}
                                    </select>
                                    <button className="linklike text-xs mt-2" onClick={() => patchTicket({ assignee_id: user.user_id })} data-testid="ticket-sidebar-assign-me">Asignarme a mí</button>
                                </div>

                                <div className="field mt-3">
                                    <label>Grupo / cola</label>
                                    <select className="select" value={ticket.group_id || ""} onChange={(e) => patchTicket({ group_id: e.target.value || null })} data-testid="ticket-sidebar-group">
                                        <option value="">— ninguno —</option>
                                        {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
                                    </select>
                                </div>

                                <div className="field mt-3">
                                    <label>Empresa</label>
                                    <select className="select" value={ticket.company_id || ""} onChange={(e) => patchTicket({ company_id: e.target.value || null })} data-testid="ticket-sidebar-company">
                                        <option value="">— sin empresa —</option>
                                        {companies.map((c) => (
                                            <option key={c.company_id} value={c.company_id}>
                                                {c.name}{c.is_placeholder ? " (placeholder)" : ""}{c.is_confidential ? " · Confidencial" : ""}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-muted text-xs" style={{ marginTop: 4 }}>Debe ser una empresa asignada al solicitante <b>{ticket.requester_name || ticket.requester_email}</b>.</p>
                                </div>
                            </>
                        )}

                        <div className="field mt-4">
                            <label>Etiquetas</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {(ticket.tags || []).length === 0 && <span className="text-dim text-sm">Sin etiquetas</span>}
                                {(ticket.tags || []).map((t) => <span key={t} className="badge">{t}</span>)}
                            </div>
                        </div>

                        <div className="field mt-4" data-testid="ticket-cc-fixed">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <label style={{ margin: 0 }}>Cc fijos del ticket</label>
                                {!ccFixedEditing && ticket.status !== "closed" && (
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        style={{ padding: "2px 6px", fontSize: 11 }}
                                        onClick={() => { setCcFixedDraft(ticket.cc_emails || []); setCcFixedEditing(true); }}
                                        data-testid="ticket-cc-fixed-edit"
                                        title="Editar la lista de Cc que se copiará en cada respuesta de este ticket"
                                    >
                                        Editar
                                    </button>
                                )}
                            </div>
                            {!ccFixedEditing && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                                    {(ticket.cc_emails || []).length === 0 && <span className="text-dim text-sm">Sin Cc fijos</span>}
                                    {(ticket.cc_emails || []).map((c) => (
                                        <span key={c} className="badge mono" style={{ fontSize: 10.5 }} data-testid={`ticket-cc-chip-${c}`}>{c}</span>
                                    ))}
                                </div>
                            )}
                            {ccFixedEditing && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                                    <CcInput
                                        value={ccFixedDraft}
                                        onChange={setCcFixedDraft}
                                        suggestions={directory}
                                        placeholder="Empieza a escribir un nombre o correo…"
                                        compact
                                        testid="ticket-cc-fixed-input"
                                    />
                                    <p className="text-muted text-xs" style={{ margin: 0, fontSize: 10.5 }}>Estos correos se copiarán automáticamente en TODAS las respuestas posteriores del ticket.</p>
                                    <div className="flex gap-2">
                                        <button type="button" className="btn btn-primary btn-sm" onClick={saveCcFixed} disabled={ccFixedSaving} data-testid="ticket-cc-fixed-save">
                                            {ccFixedSaving ? <span className="loader" /> : "Guardar"}
                                        </button>
                                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCcFixedEditing(false)} disabled={ccFixedSaving}>
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-4" style={{ paddingTop: 12, borderTop: "1px solid var(--border)", fontSize: 12, color: "var(--text-muted)" }}>
                            <div className="flex justify-between"><span>Creado</span><span className="mono">{formatDateShort(ticket.created_at)}</span></div>
                            <div className="flex justify-between mt-2"><span>Actualizado</span><span className="mono">{formatDateShort(ticket.updated_at)}</span></div>
                        </div>
                    </div>
                </div>
            </div>

            {viewerAttachment && (
                <AttachmentViewer attachment={viewerAttachment} onClose={() => setViewerAttachment(null)} />
            )}
            <Modal
                    open={thirdPartyModal}
                    onClose={() => setThirdPartyModal(false)}
                    title="Esperando a un tercero"
                    testId="third-party-modal"
                    footer={
                        <>
                            <button className="btn btn-ghost" onClick={() => setThirdPartyModal(false)} disabled={thirdPartySaving}>Cancelar</button>
                            <button
                                className="btn btn-primary"
                                onClick={async () => {
                                    setThirdPartySaving(true);
                                    try {
                                        // If user has a draft body or pending attachments, send the message and switch to third_party in one go
                                        if (body.trim() || pendingAttachments.length > 0) {
                                            await api.post(`/tickets/${id}/messages`, {
                                                body: body.trim() || "(adjunto)",
                                                is_private: isPrivate,
                                                attachment_ids: pendingAttachments.map((a) => a.attachment_id),
                                            });
                                            await api.patch(`/tickets/${id}`, { waiting_state: "third_party", waiting_third_party: thirdPartyName.trim() || null });
                                            setBody(""); setIsPrivate(false); setPendingAttachments([]);
                                            await load();
                                        } else {
                                            await patchTicket({ waiting_state: "third_party", waiting_third_party: thirdPartyName.trim() || null });
                                        }
                                        setThirdPartyModal(false);
                                    } finally { setThirdPartySaving(false); }
                                }}
                                disabled={thirdPartySaving || !thirdPartyName.trim()}
                                data-testid="third-party-save"
                            >
                                {thirdPartySaving ? <span className="loader" /> : "Guardar"}
                            </button>
                        </>
                    }
                >
                    <p className="text-muted text-sm" style={{ marginTop: 0 }}>Indica a quién estamos esperando para mover este ticket. Aparecerá visible en el encabezado.</p>
                    <div className="field">
                        <label>Nombre del tercero <span style={{ color: "#DC2626" }}>*</span></label>
                        <input
                            autoFocus
                            type="text"
                            className="input"
                            placeholder="Ej.: Proveedor Banorte, Microsoft Soporte L2, Legal externo…"
                            value={thirdPartyName}
                            maxLength={120}
                            onChange={(e) => setThirdPartyName(e.target.value)}
                            onKeyDown={async (e) => {
                                if (e.key === "Enter" && thirdPartyName.trim()) {
                                    e.preventDefault();
                                    setThirdPartySaving(true);
                                    try {
                                        await patchTicket({ waiting_state: "third_party", waiting_third_party: thirdPartyName.trim() });
                                        setThirdPartyModal(false);
                                    } finally { setThirdPartySaving(false); }
                                }
                            }}
                            data-testid="third-party-input"
                        />
                    </div>
                    {thirdPartySuggestions.length > 0 && (
                        <div className="field" data-testid="third-party-suggestions">
                            <label className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em" }}>Más usados</label>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {thirdPartySuggestions.map((s) => (
                                    <button
                                        key={s.name}
                                        type="button"
                                        onClick={() => setThirdPartyName(s.name)}
                                        data-testid={`third-party-suggestion-${s.name}`}
                                        title={`Usado ${s.count} ${s.count === 1 ? "vez" : "veces"}`}
                                        style={{
                                            padding: "5px 10px",
                                            background: thirdPartyName === s.name ? "rgba(220,38,38,.18)" : "var(--bg-2)",
                                            color: thirdPartyName === s.name ? "#fff" : "var(--text-muted)",
                                            border: `1px solid ${thirdPartyName === s.name ? "rgba(220,38,38,.45)" : "var(--border-strong)"}`,
                                            borderRadius: 999,
                                            fontSize: 12,
                                            cursor: "pointer",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                        }}
                                    >
                                        {s.name}
                                        <span style={{ fontSize: 10, opacity: 0.7 }}>×{s.count}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </Modal>

                <Modal open={mergeModalOpen} onClose={() => { if (!mergeSaving) setMergeModalOpen(false); }} title="Fusionar tickets" testId="ticket-merge-modal">
                    <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                        <div style={{ background: "var(--bg-2)", border: "1px solid var(--border)", padding: 12, borderRadius: 8, fontSize: 13 }}>
                            <div style={{ marginBottom: 6 }}><b>Ticket principal:</b> <span className="mono">{ticket.ticket_id}</span> — {ticket.subject}</div>
                            <div className="text-muted text-xs">El ticket actual quedará como principal. El otro se cerrará y su historial se anexará aquí.</div>
                        </div>
                        <div className="field">
                            <label>ID del ticket a fusionar</label>
                            <input
                                className="input mono"
                                placeholder="T-0042"
                                value={mergeTargetId}
                                onChange={(e) => { setMergeTargetId(e.target.value.toUpperCase().trim()); setMergeError(""); }}
                                maxLength={20}
                                data-testid="ticket-merge-target-id"
                                autoFocus
                            />
                            <small className="text-muted text-xs" style={{ marginTop: 4 }}>Tip: tomá el ID de la lista de tickets (formato T-NNNN).</small>
                        </div>
                        {mergeError && <div className="alert alert-error" data-testid="ticket-merge-error">{mergeError}</div>}
                        <div className="flex gap-2 justify-end">
                            <button className="btn btn-ghost btn-sm" onClick={() => setMergeModalOpen(false)} disabled={mergeSaving} data-testid="ticket-merge-cancel">Cancelar</button>
                            <button
                                className="btn btn-primary btn-sm"
                                disabled={mergeSaving || !mergeTargetId || mergeTargetId === ticket.ticket_id}
                                onClick={async () => {
                                    setMergeSaving(true);
                                    setMergeError("");
                                    try {
                                        await api.post(`/tickets/${ticket.ticket_id}/merge`, { merged_id: mergeTargetId });
                                        setMergeModalOpen(false);
                                        await load();
                                    } catch (e) { setMergeError(formatApiError(e)); }
                                    finally { setMergeSaving(false); }
                                }}
                                data-testid="ticket-merge-confirm"
                            >
                                <GitMerge size={13} /> {mergeSaving ? "Fusionando…" : "Fusionar aquí"}
                            </button>
                        </div>
                    </div>
                </Modal>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Tabs encima del card principal: Conversación / Fechas críticas.
// ---------------------------------------------------------------------------
function TicketTabs({ activeTab, setActiveTab, conversationCount, criticalDatesCount, showCriticalTab, showAuditTab }) {
    const baseBtn = {
        padding: "10px 18px",
        background: "transparent",
        border: "none",
        borderBottom: "2px solid transparent",
        color: "var(--text-muted)",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        transition: "color .15s, border-color .15s",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
    };
    const activeStyle = { color: "var(--text)", borderBottomColor: "#F97316" };

    return (
        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: -1 }} data-testid="ticket-tabs">
            <button
                type="button"
                onClick={() => setActiveTab("conversation")}
                style={{ ...baseBtn, ...(activeTab === "conversation" ? activeStyle : {}) }}
                data-testid="ticket-tab-conversation"
            >
                Conversación
                <span className="badge" style={{ fontSize: 10, padding: "1px 7px" }}>{conversationCount}</span>
            </button>
            {showCriticalTab && (
                <button
                    type="button"
                    onClick={() => setActiveTab("critical_dates")}
                    style={{ ...baseBtn, ...(activeTab === "critical_dates" ? activeStyle : {}) }}
                    data-testid="ticket-tab-critical-dates"
                >
                    📅 Fechas críticas
                    {criticalDatesCount > 0 && <span className="badge" style={{ fontSize: 10, padding: "1px 7px", background: "rgba(249,115,22,.15)", borderColor: "#F97316", color: "#FB923C" }}>{criticalDatesCount}</span>}
                </button>
            )}
            {showAuditTab && (
                <button
                    type="button"
                    onClick={() => setActiveTab("audit")}
                    style={{ ...baseBtn, ...(activeTab === "audit" ? activeStyle : {}) }}
                    data-testid="ticket-tab-audit"
                >
                    📋 Historial
                </button>
            )}
        </div>
    );
}


// ---------------------------------------------------------------------------
// Fechas Críticas — 5 parejas (fecha + evento + done) por ticket.
// Solo staff puede editar (canEdit). Customers ven en modo solo-lectura.
// Si AMBAS columnas de una fila están vacías, el backend la descarta al guardar.
// ---------------------------------------------------------------------------
function CriticalDatesCard({ ticket, canEdit = true, onSave }) {
    const MAX = 5;
    const initial = React.useMemo(() => {
        const arr = Array.isArray(ticket?.critical_dates) ? ticket.critical_dates.slice(0, MAX) : [];
        const padded = [...arr];
        while (padded.length < MAX) padded.push({ date: "", event: "", done: false });
        return padded.map((r) => ({ date: r.date || "", event: r.event || "", done: !!r.done }));
    }, [ticket?.critical_dates]);
    const [rows, setRows] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    // Resync when ticket changes (after an external patch)
    useEffect(() => { setRows(initial); }, [initial]);

    const updateRow = (idx, field, value) => {
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!canEdit) return;
        setSaving(true);
        try {
            const payload = rows
                .map((r) => ({ date: r.date || null, event: (r.event || "").trim() || null, done: !!r.done }))
                .filter((r) => r.date || r.event);
            await onSave(payload);
            setSavedAt(Date.now());
        } finally { setSaving(false); }
    };

    return (
        <div className="card" data-testid="ticket-critical-dates-card">
            <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0 }}>📅 Fechas críticas</h3>
                <span className="text-muted text-xs">
                    {canEdit ? "Hasta 5 parejas (Fecha + Evento). Marca el check cuando se cumpla." : "Solo lectura"}
                </span>
            </div>
            <form onSubmit={submit} style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                {rows.map((r, i) => {
                    const isEmpty = !r.date && !r.event;
                    const isDone = !!r.done;
                    return (
                        <div
                            key={i}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "32px 140px 1fr 30px",
                                gap: 10,
                                alignItems: "center",
                                opacity: isDone ? 0.55 : 1,
                            }}
                            data-testid={`critical-date-row-${i}`}
                        >
                            <label style={{ display: "flex", justifyContent: "center", alignItems: "center", cursor: canEdit && !isEmpty ? "pointer" : "default" }} title={isDone ? "Marcado como cumplido" : "Marcar como cumplido"}>
                                <input
                                    type="checkbox"
                                    checked={isDone}
                                    disabled={!canEdit || isEmpty}
                                    onChange={(e) => updateRow(i, "done", e.target.checked)}
                                    data-testid={`critical-date-${i}-done`}
                                    style={{ width: 18, height: 18, accentColor: "#16A34A", cursor: "pointer" }}
                                />
                            </label>
                            <input
                                type="date"
                                className="input"
                                value={r.date || ""}
                                onChange={(e) => updateRow(i, "date", e.target.value)}
                                disabled={!canEdit}
                                style={isDone ? { textDecoration: "line-through" } : undefined}
                                data-testid={`critical-date-${i}-date`}
                            />
                            <input
                                type="text"
                                className="input"
                                value={r.event || ""}
                                onChange={(e) => updateRow(i, "event", e.target.value.slice(0, 200))}
                                placeholder={`Evento ${i + 1}`}
                                maxLength={200}
                                disabled={!canEdit}
                                style={isDone ? { textDecoration: "line-through" } : undefined}
                                data-testid={`critical-date-${i}-event`}
                            />
                            {canEdit ? (
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => { updateRow(i, "date", ""); updateRow(i, "event", ""); updateRow(i, "done", false); }}
                                    title="Limpiar fila"
                                    data-testid={`critical-date-${i}-clear`}
                                    style={{ padding: "4px 8px", color: "var(--text-muted)" }}
                                >×</button>
                            ) : <span />}
                        </div>
                    );
                })}
                {canEdit && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                        <small className="text-muted" style={{ fontSize: 12 }}>
                            {savedAt ? "✓ Guardado." : "Las filas sin fecha y sin evento se descartan al guardar."}
                        </small>
                        <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="critical-dates-save">
                            {saving ? "Guardando…" : "Guardar fechas críticas"}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Timeline de auditoría del ticket: ediciones, asignaciones, fusiones, etc.
// Solo staff puede acceder. Se renderiza con iconos + colores por tipo de acción.
// ---------------------------------------------------------------------------
const ACTION_META = {
    "ticket.create":         { icon: "🎫", label: "Ticket creado",            color: "#22D3EE" },
    "ticket.update":         { icon: "✏️", label: "Actualización",            color: "#A5B4FC" },
    "ticket.status_change":  { icon: "🔄", label: "Cambio de estado",         color: "#FBBF24" },
    "ticket.assign":         { icon: "👤", label: "Asignación",               color: "#34D399" },
    "ticket.merge":          { icon: "🔗", label: "Tickets fusionados",       color: "#F97316" },
    "ticket.delete":         { icon: "🗑️", label: "Eliminado",                color: "#EF4444" },
    "ticket.message":        { icon: "💬", label: "Respuesta agregada",       color: "#A78BFA" },
    "ticket.attachment":     { icon: "📎", label: "Adjunto",                  color: "#94A3B8" },
};

function _formatAuditDetails(action, details) {
    if (!details || typeof details !== "object") return null;
    const keys = Object.keys(details).filter(k => details[k] !== null && details[k] !== "");
    if (keys.length === 0) return null;

    // Casos especiales (más legibles)
    if (action === "ticket.status_change" && details.from && details.to) {
        return <span><b>{details.from}</b> → <b style={{ color: "#F97316" }}>{details.to}</b></span>;
    }
    if (action === "ticket.assign") {
        const from = details.from || "sin asignar";
        const to = details.to || "sin asignar";
        return <span><b>{from}</b> → <b style={{ color: "#34D399" }}>{to}</b></span>;
    }
    if (action === "ticket.merge") {
        return <span>Se fusionó <b>{details.merged_id}</b>{details.merged_subject ? <> — &quot;{details.merged_subject}&quot;</> : null}</span>;
    }
    if (action === "ticket.message") {
        const preview = (details.preview || "").trim();
        const attN = details.attachment_count || 0;
        return (
            <span>
                {details.is_private ? (
                    <span className="badge" style={{ marginRight: 6, fontSize: 10, color: "#F59E0B", borderColor: "rgba(245,158,11,.5)", background: "rgba(245,158,11,.1)" }}>🔒 Nota privada</span>
                ) : null}
                {preview ? <span style={{ color: "var(--text)" }}>&quot;{preview}{preview.length >= 160 ? "…" : ""}&quot;</span> : <span className="text-muted">(sin texto)</span>}
                {attN > 0 ? <span className="text-muted text-xs" style={{ marginLeft: 8 }}>· {attN} adjunto{attN > 1 ? "s" : ""}</span> : null}
            </span>
        );
    }
    if (action === "ticket.update") {
        // Mostrar solo los nombres de los campos editados
        return <span className="text-muted">Campos: {keys.map(k => <span key={k} className="badge" style={{ marginRight: 4, fontSize: 10 }}>{k}</span>)}</span>;
    }
    // Fallback genérico: JSON compacto
    return <span className="text-muted text-xs mono">{JSON.stringify(details).slice(0, 200)}</span>;
}

function TicketAuditCard({ entries, loading }) {
    if (loading || entries === null) {
        return (
            <div className="card" data-testid="ticket-audit-card">
                <div className="card-header"><h3>📋 Historial</h3></div>
                <div style={{ padding: 30, textAlign: "center", color: "var(--text-muted)" }}>Cargando…</div>
            </div>
        );
    }
    if (!entries || entries.length === 0) {
        return (
            <div className="card" data-testid="ticket-audit-card">
                <div className="card-header"><h3>📋 Historial</h3></div>
                <div style={{ padding: 30, textAlign: "center", color: "var(--text-muted)" }}>Sin actividad registrada todavía.</div>
            </div>
        );
    }
    return (
        <div className="card" data-testid="ticket-audit-card">
            <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0 }}>📋 Historial</h3>
                <span className="text-muted text-xs">{entries.length} eventos</span>
            </div>
            <div style={{ padding: "8px 4px" }}>
                {entries.map((e, i) => {
                    const meta = ACTION_META[e.action] || { icon: "•", label: e.action, color: "var(--text-muted)" };
                    return (
                        <div
                            key={`${e.created_at}_${i}`}
                            data-testid={`audit-entry-${i}`}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "44px 1fr 130px",
                                gap: 10,
                                alignItems: "flex-start",
                                padding: "12px 14px",
                                borderBottom: i < entries.length - 1 ? "1px solid var(--border)" : "none",
                            }}
                        >
                            <div style={{
                                width: 36, height: 36, borderRadius: 8,
                                background: meta.color + "1F", border: `1px solid ${meta.color}55`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 16, flexShrink: 0,
                            }}>{meta.icon}</div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: meta.color }}>{meta.label}</span>
                                    <span className="text-muted text-xs">por <b style={{ color: "var(--text)" }}>{e.actor_email || "sistema"}</b></span>
                                </div>
                                <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.5 }}>
                                    {_formatAuditDetails(e.action, e.details)}
                                </div>
                            </div>
                            <div className="text-dim text-xs mono" style={{ textAlign: "right", whiteSpace: "nowrap", paddingTop: 2 }}>
                                {(e.created_at || "").replace("T", " ").slice(0, 16)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
